import React from 'react';
import styled from 'styled-components';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { BottomFixedContainer } from '@/app/components/hotel-detail/BottomFixedContainer';
import { EvoTripSummaryModal } from '@/app/components/hotel-detail/evo/EvoTripSummaryModal';
import { useStepKeys } from '@/app/components/hotel-detail/evo/EvoBreadcrumbs';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { keyDownHandler } from '@/app/components/hotel-detail/common/common.helper';
import { getBoundDepartureCity } from '@/app/components/hotel-detail/flight-train/TransportCommon';
import { useDestinationCityName } from '@/app/components/hotel-detail/hooks/useDestinationCityName';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { MaskIcon } from '@/app/common/icons/MaskIcon';

const SummaryBar = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: ${({ theme }) => theme.kallisto.space.space150} ${({ theme }) => theme.kallisto.space.space100}
    ${({ theme }) => theme.kallisto.space.space150} ${({ theme }) => theme.kallisto.space.space200};
`;

const PriceGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconSecondary};
`;

const ProgressBar = styled.div`
  display: flex;
  gap: 6px;
  padding: 0 ${({ theme }) => theme.kallisto.space.space50} ${({ theme }) => theme.kallisto.space.space50};
`;

const ProgressSegment = styled.div<{ $active: boolean }>`
  height: 4px;
  flex: 1;
  border-radius: 2px;
  background-color: ${({ $active, theme }) =>
    $active
      ? theme.kallisto.color.background.static.backgroundNeutralContrast
      : theme.kallisto.color.border.static.borderDisabled};
`;

const ChevronDown = styled(MaskIcon)`
  align-self: center;
`;

/**
 * Bottom-fixed trip summary panel (mobile): shows the flight origin – destination
 * and the price per person. Tapping it opens the "Your trip" modal
 * (EvoTripSummaryModal). The desktop sidebar (EvoTripOverview) replaces it on lg+.
 */
function TripSummaryBarContent({ activeStep = 1 }: { activeStep?: number }) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { selectedMealPlan, selectedBrandedFare } = useSelectedMealPlan();
  const { offersData } = useOffersContext();
  const { accommodationOnly } = useUrlSearchParams();
  const { showTotalPrice, toDisplayPrice } = usePriceDisplay();
  // Hotel Only drops the "Choose Flight" step, so the bar has three segments there, not four (MPH-935).
  const totalSteps = useStepKeys().length;

  const [isModalOpen, setModalOpen] = React.useState(false);

  // The origin is where the traveller physically leaves from, so it stays the flight's departure
  // city. The destination is the one that was searched for, not the arrival airport's city — see
  // useDestinationCityName for why, and for the hotel-city fallback behind it.
  const outbound = offersData?.transport?.transports?.[0]?.outbound;
  const originCity = outbound?.segments?.length ? getBoundDepartureCity(outbound) : undefined;
  const destinationCity = useDestinationCityName();
  const title = originCity && destinationCity ? `${originCity} – ${destinationCity}` : (destinationCity ?? '');

  const pricedOffer = selectedBrandedFare ?? selectedMealPlan;
  const perPersonPrice = pricedOffer?.finalPrice ?? 0;
  const finalPrice = showTotalPrice ? (pricedOffer?.finalTotalPrice ?? toDisplayPrice(perPersonPrice)) : perPersonPrice;
  // MPH-935: showTotalPrice is the price *toggle*, and usePriceDisplay only ever turns it on for a
  // PER_PERSON price — a total cannot be totalled again. Hotel Only is priced TOTAL, so its finalPrice is
  // already the whole-stay figure and the label has to say so rather than fall through to "per person".
  const showsTotal = showTotalPrice || accommodationOnly;

  return (
    <BottomFixedContainer
      hide={false}
      css={`
        padding: 0;
        border-radius: 16px 16px 0 0;
        box-shadow: 0px -10px 16px 0px #0000000d;

        /* The desktop sidebar replaces this bar ≥lg. First paint may mount it there
           anyway (UA-based branch while the viewport is unmeasured), so it hides itself. */
        @media ${customCss.breakpoints.lg} {
          display: none;
        }
      `}
    >
      <SummaryBar
        role="button"
        tabIndex={0}
        onClick={() => setModalOpen(true)}
        onKeyDown={keyDownHandler(() => setModalOpen(true))}
      >
        <BarRow>
          <Text
            variant="Title5"
            weight="bold"
            css={`
              /* A hub without a cityName falls back to its name ("London Gatwick"), so the
                 title has to shrink and clip instead of pushing the price out of the row. */
              min-width: 0;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            `}
          >
            {title}
          </Text>
          <PriceGroup>
            <Text
              variant="Title3"
              weight="bold"
              color="textPrimary"
              css={`
                line-height: 24px;
              `}
            >
              {formatPrice({ price: finalPrice, localization })}
            </Text>
            <Text variant="BodySmall" weight="bold" color="textPrimary">
              {t(showsTotal ? 'vacation.oss.cards.total' : 'vacation.oss.cards.pp')}
            </Text>
            <ChevronDown category="Arrows" name="icon-small-chevron-down" size={20} footprint={16} />
          </PriceGroup>
        </BarRow>
        <ProgressBar>
          {Array.from({ length: totalSteps }, (_, index) => (
            <ProgressSegment key={index} $active={index < activeStep} />
          ))}
        </ProgressBar>
      </SummaryBar>
      <EvoTripSummaryModal isOpen={isModalOpen} setOpen={setModalOpen} activeStep={activeStep} />
    </BottomFixedContainer>
  );
}

export function EvoTripSummaryBar({ activeStep = 1 }: { activeStep?: number }) {
  const { isLoadingRooms, isSoldout } = useOffersContext();
  return !isSoldout && !isLoadingRooms ? <TripSummaryBarContent activeStep={activeStep} /> : undefined;
}
